import { PixelBuffer, bayer, hash01, mix, rgb } from "../../intro/pixel_raster"
import { CreationLayout } from "./creation_layout"

// The deck the character stands on, the night before the wreck, as pixel art: planks, the bulwark and
// its moonlit cap rail, timberheads, the lantern's post and arm, the shrouds running up to the mast, and
// a coil of rope. Everything is graded toward the night; the lantern's warm light is a layer of its own
// (lightPools), added over this one.
//
// Pure: buffers in, buffers out. Colours are from the LPC style guide's palette.

export const WOOD = [0x140c0e, 0x2b1c1d, 0x3a2912, 0x56381d, 0x63462b, 0x7e6952].map(h => rgb(h))
const IRON = [0x101414, 0x1c2222, 0x2a3034].map(h => rgb(h))
const BRASS = [0x4b2b13, 0x986a20, 0xb78c41, 0xd9b75c].map(h => rgb(h))
const PANE = rgb(0xffc56b)
const CORE = rgb(0xfff0b8)
const WHITE_HOT = rgb(0xffffff)
const NIGHT = rgb(0x141d31)
const MOONLIT = rgb(0x8292b4)
const ROPE = rgb(0x3d302e)
const COIL = [rgb(0x6b5a45), rgb(0x4a3c2e)]
const INK = rgb(0x0b0b13)

const grade = (c: number, k: number) => mix(c, NIGHT, k)

/** What each pixel of the deck layer is, for the light and the shadow: nothing, plank, rail, or post and rigging. */
export const DeckKind = { None: 0, Plank: 1, Rail: 2, Fitting: 3 } as const

export interface DeckArt {
    /** Covers the whole view; empty above the rail but for the post and rigging. */
    buffer: PixelBuffer
    kind: Uint8Array
}

export const paintDeck = (layout: CreationLayout): DeckArt => {
    const { view, railY, deckY, postX, lanternX } = layout
    const buffer = new PixelBuffer(view.w, view.h, view.x, view.y)
    const kind = new Uint8Array(view.w * view.h)
    const right = view.x + view.w
    const bottom = view.y + view.h
    const set = (x: number, y: number, c: number, k: number) => {
        const i = buffer.index(x, y)
        if (i >= 0) {
            buffer.data[i] = c
            kind[i] = k
        }
    }

    // Planks running athwart the deck, wider toward you, butt joints staggered row to row.
    let y0 = deckY
    for (let r = 0; y0 < bottom; r++) {
        const h = Math.min(7, 3 + Math.floor(r / 2))
        const joints: number[] = []
        for (let x = ((r * 29) % 53) - 10 + Math.floor(view.x / 52) * 52 - 52; x < right; x += 52 + ((r * 17) % 13)) joints.push(x)
        for (let j = 0; j < h; j++) {
            const y = y0 + j
            for (let x = view.x; x < right; x++) {
                const n = hash01(x * 7919 + y * 104729)
                let c = j === 0 ? WOOD[1] : j === 1 ? WOOD[4] : r % 2 ? WOOD[3] : WOOD[4]
                if (j > 1 && n > 0.93) c = WOOD[2]
                if (j > 1 && n < 0.04) c = WOOD[5]
                if (joints.includes(x)) c = WOOD[1]
                if (h > 3 && (j === 1 || j === h - 1) && joints.some(jx => x === jx - 2 || x === jx + 2)) c = WOOD[0]
                set(x, y, grade(c, Math.max(0.2, 0.48 - 0.04 * r)), DeckKind.Plank)
            }
        }
        y0 += h
    }

    // The bulwark: its inner face in the moon's shadow, the cap rail rimmed by moonlight.
    for (let y = railY; y < deckY; y++) {
        const k = y - railY
        for (let x = view.x; x < right; x++) {
            let c: number
            if (k === 0) c = mix(grade(WOOD[5], 0.3), MOONLIT, 0.35)
            else if (k === 1) c = grade(WOOD[4], 0.42)
            else if (k === 2) c = grade(WOOD[0], 0.3)
            else if ((k - 3) % 4 === 0) c = grade(WOOD[1], 0.5)
            else c = grade((k - 3) % 4 === 1 ? WOOD[3] : WOOD[2], 0.55)
            if (k === deckY - railY - 1) c = grade(WOOD[0], 0.2)
            set(x, y, c, DeckKind.Rail)
        }
    }
    // Timberheads standing up through the rail, every 46 pixels of the ship.
    for (let x = 14 + Math.floor((view.x - 14) / 46) * 46; x < right; x += 46) {
        for (let y = railY - 3; y < deckY; y++) {
            set(x, y, grade(WOOD[4], y < railY ? 0.25 : 0.5), DeckKind.Rail)
            set(x + 1, y, grade(WOOD[3], 0.5), DeckKind.Rail)
            set(x + 2, y, grade(WOOD[1], 0.45), DeckKind.Rail)
        }
        set(x, railY - 3, mix(grade(WOOD[5], 0.3), MOONLIT, 0.35), DeckKind.Rail)
        set(x + 1, railY - 3, mix(grade(WOOD[5], 0.3), MOONLIT, 0.25), DeckKind.Rail)
    }
    // Scuppers where the deck meets the bulwark.
    for (const x of [60, 152, 318]) {
        for (let i = 0; i < 5; i++) set(x + i, deckY - 2, INK, DeckKind.Rail)
    }

    // The lantern's post up past the rail, its iron arm and a short chain.
    for (let y = layout.lanternBase - 21; y <= deckY; y++) {
        set(postX, y, grade(WOOD[5], 0.35), DeckKind.Fitting)
        set(postX + 1, y, grade(WOOD[3], 0.4), DeckKind.Fitting)
        set(postX + 2, y, grade(WOOD[1], 0.35), DeckKind.Fitting)
    }
    set(postX, layout.lanternBase - 22, grade(WOOD[5], 0.2), DeckKind.Fitting)
    set(postX + 1, layout.lanternBase - 22, grade(WOOD[4], 0.3), DeckKind.Fitting)
    const armY = layout.lanternBase - 18
    for (let x = postX + 3; x <= lanternX + 4; x++) set(x, armY, IRON[2], DeckKind.Fitting)
    for (let k = 0; k < 4; k++) set(postX + 3 + k, armY + 4 - k, IRON[1], DeckKind.Fitting)
    set(lanternX + 3, armY + 1, IRON[2], DeckKind.Fitting)
    set(lanternX + 3, armY + 3, IRON[1], DeckKind.Fitting)

    // The shrouds running up to the masthead off the top of the screen, their ratlines, and the deadeyes.
    const climb = 0.42
    const shroudX = (x0: number, y: number) => Math.round(x0 + (railY - 1 - y) * climb)
    for (const x0 of layout.shrouds) {
        for (let y = railY - 1; y >= view.y; y--) {
            const x = shroudX(x0, y)
            if (x >= right) break
            set(x, y, grade(ROPE, 0.15), DeckKind.Fitting)
        }
        set(x0 - 1, railY - 2, grade(WOOD[1], 0.2), DeckKind.Fitting)
        set(x0, railY - 2, grade(WOOD[4], 0.3), DeckKind.Fitting)
        set(x0 + 1, railY - 2, grade(WOOD[1], 0.2), DeckKind.Fitting)
        set(x0, railY - 3, grade(WOOD[3], 0.3), DeckKind.Fitting)
    }
    const first = layout.shrouds[0]
    const last = layout.shrouds[layout.shrouds.length - 1]
    for (let y = railY - 9; y > view.y; y -= 7) {
        for (let x = shroudX(first, y); x <= shroudX(last, y) && x < right; x++) set(x, y, grade(ROPE, 0.3), DeckKind.Fitting)
    }

    // A coil of rope at the foot of the post, in the lantern's light.
    const cx = postX - 15
    const cy = deckY + 8
    for (let y = cy - 3; y <= cy + 3; y++) {
        for (let x = cx - 8; x <= cx + 8; x++) {
            const d = Math.hypot((x - cx) / 8, (y - cy) / 3.2)
            if (d > 1 || d < 0.38) continue
            const c = COIL[Math.floor(d * 5) % 2 ? 0 : 1]
            set(x, y, grade(y < cy ? mix(c, rgb(0xfff6e8), 0.1) : c, 0.2), DeckKind.Plank)
        }
    }
    for (let x = cx - 8; x <= cx + 8; x++) set(x, cy + 4, grade(INK, 0.1), DeckKind.Plank)

    return { buffer, kind }
}

/**
 * The lantern's warm light as a layer to ADD over the deck: stepped pools on the planks under it, a glow on
 * the rail and post, and the air round the glass. Little green, so added over the night it reads as fire
 * and not as grey. Drawn at full strength; the page dims and flickers it with the layer's alpha.
 */
export const lightPools = (layout: CreationLayout, deck: DeckArt): PixelBuffer => {
    const { view, light, deckY, feetY } = layout
    const buffer = new PixelBuffer(view.w, view.h, view.x, view.y)
    const add = (x: number, y: number, amount: number) => {
        const i = buffer.index(x, y)
        if (i < 0 || amount <= 0) return
        const level = Math.min(1, amount)
        buffer.data[i] = rgb((Math.round(255 * level) << 16) | (Math.round(184 * level) << 8) | Math.round(96 * level))
    }
    const steps = (a: number, n: number) => Math.round(Math.min(1, Math.max(0, a)) * n) / n
    // Pools on the planks, flattened onto the deck, dithered at their edges.
    const gy = feetY - 6
    const pools: [number, number][] = [[13, 0.36], [27, 0.2], [46, 0.08]]
    for (let y = deckY; y < view.y + view.h; y++) {
        for (let x = Math.floor(light.x - 50); x <= light.x + 50; x++) {
            const i = buffer.index(x, y)
            if (i < 0 || !deck.kind[i]) continue
            const d = Math.hypot(x + 0.5 - light.x, (y + 0.5 - gy) / 0.45) + (bayer(x, y) - 0.5) * 2.4
            const ring = pools.findIndex(([r]) => d < r)
            if (ring >= 0) add(x, y, steps(pools[ring][1], 20))
        }
    }
    // The rail, the post and the air round the glass, by distance from the flame.
    for (let y = Math.floor(light.y - 24); y < deckY; y++) {
        for (let x = Math.floor(light.x - 30); x <= light.x + 30; x++) {
            const i = buffer.index(x, y)
            if (i < 0) continue
            const d = Math.hypot(x + 0.5 - light.x, y + 0.5 - light.y) + (bayer(x, y) - 0.5) * 1.8
            const onWood = deck.kind[i] >= DeckKind.Rail
            const a = onWood ? (d < 9 ? 0.42 : d < 18 ? 0.22 : d < 28 ? 0.08 : 0) : d < 4 ? 0.42 : d < 8 ? 0.18 : d < 13 ? 0.07 : 0
            if (a) add(x, y, steps(a, 12))
        }
    }
    return buffer
}

export const LANTERN_W = 7
export const LANTERN_H = 14
/** The flame steps through this many shapes. */
export const FLAME_FRAMES = 4

/** A ship's stern lantern: an iron ring and cap, a brass frame, glass full of light, the flame one of FLAME_FRAMES. */
export const paintLantern = (flame: number, lit: boolean = true): PixelBuffer => {
    const buffer = new PixelBuffer(LANTERN_W, LANTERN_H)
    const px = (dx: number, dy: number, c: number) => buffer.set(dx, LANTERN_H - 1 - dy, c)
    const [ironDark, iron, ironLit] = IRON
    const [line, brass, brassLit, brassHi] = BRASS
    px(2, 13, ironLit)
    px(3, 13, ironLit)
    px(4, 13, iron)
    px(1, 12, ironLit)
    px(5, 12, ironDark)
    for (let dx = 1; dx <= 5; dx++) px(dx, 11, dx < 3 ? brassLit : brass)
    for (let dx = 0; dx <= 6; dx++) px(dx, 10, dx === 0 ? brassHi : dx === 6 ? line : dx < 3 ? brassLit : brass)
    for (let dy = 4; dy <= 9; dy++) {
        px(0, dy, dy === 9 ? brassLit : brass)
        px(6, dy, line)
        for (let dx = 1; dx <= 5; dx++) {
            const inner = dx >= 2 && dx <= 4 && dy >= 5 && dy <= 8
            px(dx, dy, !lit ? (dx === 1 ? ironLit : iron) : inner ? CORE : PANE)
        }
    }
    if (lit) {
        const f = ((flame % FLAME_FRAMES) + FLAME_FRAMES) % FLAME_FRAMES
        px(3, 7, WHITE_HOT)
        px(f === 1 ? 2 : f === 3 ? 4 : 3, 8, CORE)
        px(3, 5, f === 2 ? PANE : CORE)
    }
    for (let dx = 0; dx <= 6; dx++) px(dx, 3, dx === 0 ? brassHi : dx === 6 ? line : brassLit)
    for (let dx = 1; dx <= 5; dx++) px(dx, 2, brass)
    for (let dx = 1; dx <= 5; dx++) px(dx, 1, line)
    return buffer
}
